import {
  ICONS_BY_PLAYBOOK_DOMAIN,
  gridBreakpoints,
  gridCols,
  gridMargins,
} from "@modules/tasks/constants";
import { FC, useCallback, useEffect, useState } from "react";
import { Layout, Responsive, WidthProvider } from "react-grid-layout";
import TaskItem from "../TaskItem";
import {
  useDisabledCallToActionTooltipsByTaskTemplateId,
  useGridLayouts,
  useOrderedOpenTasks,
} from "@modules/tasks/hooks";
import { useCallToActions } from "@modules/tasks/hooks.web";
import ZeroTaskState from "../ZeroTaskState";
import Measure, { ContentRect } from "react-measure";
import useTasksStore from "@modules/tasks/state";
import { useRouter } from "@common/hooks";
import { ProductPhase, ProductTopic, TaskCategory } from "@modules/tasks/types";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const TopicIcon: FC<{ category: TaskCategory | ProductTopic }> = ({
  category,
}) => {
  if (category === "Onboarding") {
    category = ProductPhase.PHASE_1 as unknown as ProductTopic;
  }
  const IconComponent = ICONS_BY_PLAYBOOK_DOMAIN[category as ProductTopic];
  if (!IconComponent) {
    return null;
  }
  return (
    <div className="h-8 w-8">
      <IconComponent width={32} height={32} color="black" />
    </div>
  );
};
interface TasksGridProps {
  tasks: Forme.EnrichedTask[];
  isAdvisor: boolean;
  isCompleted: boolean;
  product: string | undefined;
}

const TasksGrid: FC<TasksGridProps> = ({
  tasks,
  isAdvisor,
  isCompleted,
  product,
}) => {
  const { setSelectedTask, setAddNewTaskModalVisible } = useTasksStore();
  const router = useRouter();
  const { doAction } = useCallToActions({ router });
  const { getLayout } = useGridLayouts();
  const [layouts, setLayouts] = useState<Record<string, Layout[]>>(
    getLayout(tasks, {})
  );

  const [taskHeightById, setTaskHeightById] = useState<Record<string, number>>(
    {}
  );

  const onEditTask = (id: string) => {
    const task = tasks?.find((t) => t.id === id);
    if (task) {
      setSelectedTask(task);
      setAddNewTaskModalVisible(true);
    }
  };
  const onResize = useCallback(
    (contentRect: ContentRect, taskId: Forme.EnrichedTask["id"]) => {
      if (contentRect.bounds) {
        const height = contentRect.bounds.height;
        setTaskHeightById((prev) => ({
          ...prev,
          [taskId]: height,
        }));
      }
    },
    []
  );

  const orderedOpenTasks = useOrderedOpenTasks(tasks, isCompleted);

  const disabledCallToActionTooltipsByTaskTemplateId =
    useDisabledCallToActionTooltipsByTaskTemplateId();

  useEffect(() => {
    setLayouts(getLayout(orderedOpenTasks, taskHeightById));
  }, [orderedOpenTasks, getLayout, taskHeightById, isCompleted]);

  return (
    <>
      {orderedOpenTasks?.length ? (
        <div className="flex flex-col gap-8 sm:gap-4">
          {orderedOpenTasks.map((task) => (
            <div key={task.id}>
              <ResponsiveReactGridLayout
                key={task.id}
                className="layout max-w-[1060px] rounded-lg"
                rowHeight={1}
                margin={gridMargins}
                cols={gridCols}
                breakpoints={gridBreakpoints}
                containerPadding={[0, 0]}
                layouts={layouts}
              >
                <div key={task.id}>
                  <Measure
                    onResize={(contentRect) => {
                      onResize(contentRect, task.id);
                    }}
                    bounds
                  >
                    {({ measureRef }) => (
                      <div ref={measureRef} className="flex flex-col">
                        <TaskItem
                          name={task.name}
                          description={task.description}
                          taskId={task.id}
                          callToAction={task.callToAction}
                          onAction={doAction}
                          onEditTask={onEditTask}
                          isCompleted={isCompleted}
                          isAdvisor={isAdvisor}
                          estimatedCompletionTime={task.estimatedCompletionTime}
                          attachedNote={task.attachedNote}
                          disabledCallToActionTooltip={
                            disabledCallToActionTooltipsByTaskTemplateId?.[
                              task.templateId || ""
                            ]
                          }
                          ctaText={task.ctaText}
                          topicIcon={<TopicIcon category={task.category} />}
                          productTopic={task.productTopic}
                          dueDate={task.dueDate}
                        />
                      </div>
                    )}
                  </Measure>
                  <div className="inline-block h-20" />
                </div>
              </ResponsiveReactGridLayout>
            </div>
          ))}
        </div>
      ) : (
        <ZeroTaskState
          title={
            isCompleted
              ? "You have no completed tasks"
              : `Congrats! All your tasks are done.${
                  product ? " Your financial advisor is hard at work." : ""
                }`
          }
        />
      )}
    </>
  );
};

export default TasksGrid;
